import { memo, useState, useEffect, useMemo } from 'react';
import Autoplay from 'embla-carousel-autoplay';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { buildAssetUrl } from 'root/src/client/v2/common/helpers/assetUrl';
import { getDropAssetImageUrls } from 'root/src/shared/drops/helpers/dropAsset.helpers';
import { Root, Container, Slide, } from 'root/src/client/v2/common/components/Carousel/Carousel';
import { useCarouselAutoplay, useStopAutoplayOnEmbedFocus } from 'root/src/client/v2/common/hooks/useCarouselAutoplay';
import { cn } from 'root/src/client/v2/common/helpers/cn';
import { Img } from 'root/src/client/v2/common/components/ui/image';
const getYoutubeId = (url) => {
    if (!url) {
        return undefined;
    }
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([\w-]{11})/);
    return match === null || match === void 0 ? void 0 : match[1];
};
const arrowClass = 'absolute top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition-colors hover:bg-black/70 disabled:opacity-40';
// Arrows and dots live together so every manual step goes through stopAutoplay first. The shared
// Carousel buttons scroll straight through the embla api, which does not re-arm the autoplay
// countdown, so a click landing late in the 6s window used to be overtaken by the timer and the
// slide the visitor picked flashed past to the next one.
const MediaControls = memo(({ count }) => {
    const { emblaApi, stopAutoplay } = useCarouselAutoplay();
    const [selected, setSelected] = useState(0);
    useStopAutoplayOnEmbedFocus(stopAutoplay);
    useEffect(() => {
        if (!emblaApi) {
            return undefined;
        }
        const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
        onSelect();
        emblaApi.on('select', onSelect).on('reInit', onSelect);
        return () => {
            emblaApi.off('select', onSelect).off('reInit', onSelect);
        };
    }, [emblaApi]);
    const scrollPrev = () => {
        stopAutoplay();
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
    };
    const scrollNext = () => {
        stopAutoplay();
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
    };
    const scrollTo = (index) => {
        stopAutoplay();
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index);
    };
    return (<>
			<button type="button" onClick={scrollPrev} aria-label="Previous" className={cn(arrowClass, 'left-3')}>
				<ChevronLeftIcon sx={{ fontSize: 20 }}/>
			</button>
			<button type="button" onClick={scrollNext} aria-label="Next" className={cn(arrowClass, 'right-3')}>
				<ChevronRightIcon sx={{ fontSize: 20 }}/>
			</button>
			<div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
				{Array.from({ length: count }).map((_, index) => (<button 
        // eslint-disable-next-line react/no-array-index-key
        key={index} type="button" onClick={() => scrollTo(index)} aria-label={`Go to slide ${index + 1}`} className={cn('h-2 rounded-full transition-all', index === selected ? 'w-8 bg-primary' : 'w-2 bg-white/40 hover:bg-white/60')}/>))}
			</div>
		</>);
});
export const DropMediaSection = memo(({ drop }) => {
    const slides = useMemo(() => {
        const youtubeId = getYoutubeId(drop.youtubeURL);
        const imageSlides = getDropAssetImageUrls(drop.assets)
            .map(src => buildAssetUrl(src, { w: 1280, q: 80, fmt: 'webp' }))
            .filter((src) => Boolean(src))
            .map(src => ({ type: 'image', src }));
        return [...(youtubeId ? [{ type: 'video', id: youtubeId }] : []), ...imageSlides];
    }, [drop.youtubeURL, drop.assets]);
    // Slow autoplay, purely a courtesy for someone who is not touching the carousel. stopOnInteraction
    // turns hover and drag into a permanent stop rather than a pause, and MediaControls stops it on
    // arrow/dot clicks, so once the visitor takes over (or starts the trailer) the timer never slides
    // anything away underneath them. Memoised because a fresh plugin instance per render re-inits the
    // carousel and restarts the countdown.
    const autoplayPlugins = useMemo(() => (slides.length > 1 ? [Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true })] : []), [slides.length]);
    if (!slides.length) {
        return null;
    }
    return (<section id="media" className="scroll-mt-40 px-4 py-12 sm:px-6 lg:px-8">
			<div className="mx-auto max-w-7xl">
				<h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">Media</h2>
				<div className="relative mt-6 overflow-hidden rounded-2xl border border-solid border-white/[0.06] bg-card">
					<Root loop={slides.length > 1} plugins={autoplayPlugins} className="relative">
						<Container className="flex">
							{slides.map(slide => (<Slide key={slide.type === 'video' ? `video-${slide.id}` : slide.src} width="100%" className="relative aspect-video">
									{slide.type === 'image' ? (<Img src={slide.src} alt="Drop media" className="h-full w-full object-cover"/>) : (<iframe title="Drop trailer" src={`https://www.youtube.com/embed/${slide.id}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="h-full w-full"/>)}
								</Slide>))}
						</Container>
						{slides.length > 1 && <MediaControls count={slides.length}/>}
					</Root>
				</div>
			</div>
		</section>);
});
